import { measureLatency } from '../../../gctl-measure';
import {
  PrivateCommentReplyFields,
  PrivateCommentReply,
} from '../../../models/components/schemas/private-comment-reply/PrivateCommentReply';
import {
  Select,
  NetworkError,
  deepCamelCase,
  deepSnakeCase,
  searchQueryString,
  intoSnakeCase,
} from '../../../fetchers/_helpers';

/**
 * This file was automatically generated by @gctl/openapi-fetchers
 * Do not modify this file, any changes will be destroyed when
 * the command is rerun.
 */

export type GetVideoPrivateCommentRepliesFields = readonly PrivateCommentReplyFields[];

/** Options for GET /videos/{video_id}/private_comments/{comment_id}/replies */
export type GetVideoPrivateCommentRepliesOptions<Paths extends readonly string[]> = Omit<
  RequestInit,
  'method' | 'body'
> & {
  baseUrl?: string;

  /** Available fields on the response type, application/json */
  select: Paths;
  where: {
    /**
     * The ID of the video.
     */
    videoId: number;
    /**
     * The ID of the private comment.
     */
    commentId: number;
  };

  /** Possible query parameters for /videos/{video_id}/private_comments/{comment_id}/replies */
  query?: {
    /**
     * The sort direction of the results.
     *
     * Option descriptions:
     *  * `asc` - Sort the results in ascending order.
     *  * `desc` - Sort the results in descending order.
     *
     */
    direction?: `asc` | `desc`;

    /**
     * The page number of the results to show.
     */
    page?: number;

    /**
     * The password for the review link.
     */
    password?: string;

    /**
     * The number of items to show on each page of results, up to a maximum of 100.
     */
    perPage?: number;

    /**
     * The ID of the review link used as context.
     */
    reviewId?: string;
  };
};

/** Result type for GET /videos/{video_id}/private_comments/{comment_id}/replies */
export type GetVideoPrivateCommentRepliesResult<Paths extends readonly string[]> = {
  /** The total number of results for GET /videos/{video_id}/private_comments/{comment_id}/replies */
  total: number;
  /** The current page */
  page: number;
  /** The number of results per page specified in the request */
  perPage: number;
  /** Available API endpoints for traversing the paginated data */
  paging: {
    /** The next page, if available */
    next: string | null;
    /** The previous page, if available */
    previous: string | null;
    /** The first page */
    first: string;
    /** The last page */
    last: string;
  };
  /** An array containing the current results */
  data: Select<PrivateCommentReply, Paths>[];
};

/**
 * Get all the replies to a private comment
 * This method returns every reply for the specified private comment.
 */
export async function getVideoPrivateCommentReplies<
  Paths extends GetVideoPrivateCommentRepliesFields,
>({
  baseUrl,
  select,
  where: { videoId, commentId },
  query,
  ...options
}: GetVideoPrivateCommentRepliesOptions<Paths>): Promise<
  GetVideoPrivateCommentRepliesResult<Paths>
> {
  return measureLatency('getVideoPrivateCommentReplies', 'GET', async () => {
    let res = await fetch(
      `${baseUrl}/videos/${videoId}/private_comments/${commentId}/replies?${searchQueryString(
        query,
      )}&fields=${select.map(intoSnakeCase).join(',')}`,
      {
        ...options,
        method: 'GET',
      },
    );

    if (!res.ok) {
      throw new NetworkError('A network error occurred', res.status, res);
    }

    if (res.status === 204) {
      return null as unknown as GetVideoPrivateCommentRepliesResult<Paths>;
    }

    if (!res.headers.get('content-type')?.match(/^application\/(.+)?json$/)) {
      throw new Error('Expected JSON response');
    }

    let json = await res.json();
    return deepCamelCase(json);
  });
}

export type PostVideoPrivateCommentRepliesFields = readonly PrivateCommentReplyFields[];

/** Options for POST /videos/{video_id}/private_comments/{comment_id}/replies */
export type PostVideoPrivateCommentRepliesOptions<Paths extends readonly string[]> = Omit<
  RequestInit,
  'method' | 'body'
> & {
  baseUrl?: string;

  /** Available fields on the response type, application/json */
  select: Paths;
  where: {
    /**
     * The ID of the video.
     */
    videoId: number;
    /**
     * The ID of the private comment.
     */
    commentId: number;
  };

  /** Possible query parameters for /videos/{video_id}/private_comments/{comment_id}/replies */
  query?: {
    /**
     * The password for the review link.
     */
    password?: string;

    /**
     * The ID of the review link used as context.
     */
    reviewId?: string;
  };

  /** Data serialized to body for POST /videos/{video_id}/private_comments/{comment_id}/replies */
  variables: {
    /**
     * The ID of the guest user.
     * @example e58ed763-928c-4155-bee9-fdbaaadc15f3a
     */

    guestUserId?: string;

    /**
     * The guest name used when the user isn't logged in.
     * @example John Doe
     */

    name: string;

    /**
     * The rich comment in JSON stringified form.
     * @example {"type":"doc","content":[{"type":"paragraph","content":[{"type":"text","text":"Hi Tom"}]}]}
     */

    richtext?: string;

    /**
     * The reply text.
     * @example Great video!
     */

    text: string;
  };
};

/** Result type for POST /videos/{video_id}/private_comments/{comment_id}/replies */
export type PostVideoPrivateCommentRepliesResult<Paths extends readonly string[]> = Select<
  PrivateCommentReply,
  Paths
>;

/**
 * Add a reply to a private comment
 * This method adds a reply to the specified private comment.
 */
export async function postVideoPrivateCommentReplies<
  Paths extends PostVideoPrivateCommentRepliesFields,
>({
  baseUrl,
  select,
  variables,
  where: { videoId, commentId },
  query,
  ...options
}: PostVideoPrivateCommentRepliesOptions<Paths>): Promise<
  PostVideoPrivateCommentRepliesResult<Paths>
> {
  return measureLatency('postVideoPrivateCommentReplies', 'POST', async () => {
    let res = await fetch(
      `${baseUrl}/videos/${videoId}/private_comments/${commentId}/replies?${searchQueryString(
        query,
      )}&fields=${select.map(intoSnakeCase).join(',')}`,
      {
        ...options,
        method: 'POST',
        body: JSON.stringify(deepSnakeCase(variables)),
      },
    );

    if (!res.ok) {
      throw new NetworkError('A network error occurred', res.status, res);
    }

    if (res.status === 204) {
      return null as unknown as PostVideoPrivateCommentRepliesResult<Paths>;
    }

    if (!res.headers.get('content-type')?.match(/^application\/(.+)?json$/)) {
      throw new Error('Expected JSON response');
    }

    let json = await res.json();
    return deepCamelCase(json);
  });
}
